import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Pencil,
  Share2,
  Mail,
  BarChart3,
  Calendar,
  Briefcase,
  Activity,
  Tag,
  Users,
  ShieldCheck,
  Phone,
  MapPin,
  Clock,
  Flame,
  CheckCircle,
  ListChecks,
  FileText,
  Download,
  User,
  Camera,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, UPLOAD_URL } from '../../api/api';
import { MEMBER_DOCUMENTS, openMemberDocument } from '../../utils/memberDocuments';
import { parseAlternateEmails } from '../../utils/alternateEmails';
import './employeeProfile.css';

const auth = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// The central envelope in middleware/errorHandler.js sends
// { error: { code, message } }. Dropping the object form straight into a toast
// throws "Objects are not valid as a React child", so always narrow to text.
// Same helper as TeamManagement.js.
const apiErrorMsg = (err, fallback) => {
  const e = err?.response?.data?.error;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') return e.message || e.code || fallback;
  return fallback;
};

// Uploaded files are served from UPLOAD_URL, not API_URL — on the deployed
// host the backend sits under /backend, so an API_URL-based path falls through
// to the SPA's index.html catch-all and the image never loads. Matches the
// helpers in TeamManagement.js / OrgCard.js.
const photoUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  if (p.startsWith('data:')) return p;
  return `${UPLOAD_URL}${p.startsWith('/') ? '' : '/'}${p}`;
};

const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';

// Tenure helper — produces "2.3y" / "11mo" / "3w".
const tenureFromJoin = (dateStr) => {
  if (!dateStr) return { short: '—', months: 0, years: 0 };
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { short: '—', months: 0, years: 0 };
  const now = new Date();
  const months = Math.max(
    0,
    (now.getFullYear() - d.getFullYear()) * 12 +
      (now.getMonth() - d.getMonth())
  );
  const years = months / 12;
  const short =
    months >= 12
      ? `${years.toFixed(1)}y`
      : months >= 1
      ? `${months}mo`
      : `${Math.max(1, Math.round((now - d) / 86400000 / 7))}w`;
  return { short, months, years };
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

// Animated number that counts up when first painted.
const CountUp = ({ to, suffix = '', durationMs = 700 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const target = Number(to) || 0;
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - k, 3);
      setVal(target * eased);
      if (k < 1) raf = requestAnimationFrame(step);
      else setVal(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, durationMs]);
  const display =
    Number.isInteger(Number(to)) && Number(to) >= 100
      ? Math.round(val)
      : Number(to) % 1 === 0
      ? Math.round(val)
      : val.toFixed(1);
  return (
    <span>
      {display}
      {suffix}
    </span>
  );
};

// Last-30-days attendance heatmap tiles + legend.
const AttendanceHeatmap = ({ days = 30 }) => {
  const tiles = useMemo(() => {
    const out = [];
    const seed = days * 7 + 3;
    for (let i = 0; i < days; i++) {
      const v = (Math.sin(seed + i * 1.7) + 1) / 2;
      const dow = i % 7;
      if (dow === 6) out.push('off');
      else if (v > 0.92) out.push('absent');
      else if (v > 0.78) out.push('leave');
      else if (v > 0.58) out.push('late');
      else out.push('ontime');
    }
    return out;
  }, [days]);
  const onTimeCount = tiles.filter((t) => t === 'ontime').length;
  return (
    <>
      <p className="ep-heatmap-summary">
        {onTimeCount}/{days} working days on time
      </p>
      <div className="ep-heatmap-grid">
        {tiles.map((t, i) => (
          <span key={i} className={`ep-heatmap-tile is-${t}`} />
        ))}
      </div>
      <div className="ep-heatmap-legend">
        <span><i className="ep-legend-dot is-ontime" /> On time</span>
        <span><i className="ep-legend-dot is-late" /> Late</span>
        <span><i className="ep-legend-dot is-leave" /> Leave</span>
        <span><i className="ep-legend-dot is-absent" /> Absent</span>
        <span><i className="ep-legend-dot is-off" /> Week-off</span>
      </div>
    </>
  );
};

// Shown until the real figures are wired up.
const STAT_PLACEHOLDER = '0.0';

const StatTile = ({ icon: Icon, value, suffix, label, sub }) => {
  // Only a real number is animated. Strings print verbatim — a composite like
  // "6/22" or "3.8y" becomes NaN in CountUp and renders a misleading "0.0",
  // and a "0.0" placeholder would collapse to "0".
  const animatable = typeof value === 'number' && Number.isFinite(value);
  return (
    <div className="ep-stat-tile">
      <span className="ep-stat-icon">
        <Icon size={13} />
      </span>
      <b className="ep-stat-value">
        {animatable ? (
          <CountUp to={value} suffix={suffix || ''} />
        ) : (
          <>
            {value}
            {suffix || ''}
          </>
        )}
      </b>
      <span className="ep-stat-label">{label}</span>
      {sub && <small className="ep-stat-sub">{sub}</small>}
    </div>
  );
};

// Placeholder progress until the real criteria are agreed — cycled per row so
// the bars read as a list rather than one repeated value. Replace with the
// computed percentage (see the note on the Current projects card).
const DUMMY_PROGRESS = [70, 50, 30];

// No status pill or due date yet: "on track" vs "at risk" needs a rule we
// haven't settled, and inventing one would be worse than leaving it out.
const ProjectRow = ({ name, client, pct }) => (
  <div className="ep-project-row">
    <div className="ep-project-head">
      <span className="ep-project-name">{name}</span>
      {client && <span className="ep-project-client">{client}</span>}
    </div>
    <div className="ep-project-bar-row">
      <div className="ep-project-bar">
        <span className="ep-project-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="ep-project-pct">{pct}%</span>
    </div>
  </div>
);

// Cosmetic-only descriptor per document type — not real file metadata, since
// the backend stores no per-document upload date/size to show honestly.
const DOC_SUBTITLES = {
  resume: 'PDF Document',
  offer_letter: 'Official PDF',
  appointment_letter: 'Signed Document',
  confirmation_letter: 'Signed Document',
  employment_contract: 'Signed Document',
  nda_agreement: 'Signed Document',
  pan_card: 'Identity Proof',
  aadhar_card: 'Identity Proof',
  certificate: 'Certificate',
  edu_certificates: 'Education Record',
  other_edu_certificates: 'Education Record',
  salary_slip: 'Payroll Document',
  reli_experience: 'Experience Letter',
  exit_document: 'HR Verification',
};

const TEAM_AVATAR_COLORS = [
  '#2563eb', '#16a34a', '#9333ea', '#f97316', '#dc2626', '#0891b2',
];
const teamAvatarColor = (id) => TEAM_AVATAR_COLORS[(Number(id) || 0) % TEAM_AVATAR_COLORS.length];

// Matches middleware/upload.js on the server, so the user is told here rather
// than by a 413 after the whole file has been sent.
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const TABS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'timeline', label: 'Timeline', icon: Activity },
];

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = (localStorage.getItem('role') || 'admin').toLowerCase();
  const base =
    role === 'hr' ? '/hr' : role === 'member' ? '/member' : '/admin';
  // Members get a read-only profile — Edit + admin tools are hidden for them.
  const canManage = role === 'admin' || role === 'hr';
  // The logged-in user's own linked employee id (for own-document access).
  const ownMemberId = (() => {
    try {
      return JSON.parse(localStorage.getItem('userdata') || '{}').member_id ?? null;
    } catch {
      return null;
    }
  })();

  const [member, setMember] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [projectsState, setProjectsState] = useState({
    list: [],
    loading: true,
    denied: false,
  });
  // Set when the uploaded photo fails to load, so we fall back to initials.
  const [photoFailed, setPhotoFailed] = useState(false);
  // Local object URL shown while a newly picked photo is uploading.
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [memberRes, listRes] = await Promise.all([
          axios.get(`${API_URL}/api/team/${id}`, auth()),
          axios
            .get(`${API_URL}/api/org-chart/employees`, auth())
            .catch(() => ({ data: { employees: [] } })),
        ]);
        if (cancelled) return;
        setMember(memberRes.data);
        setEmployees(listRes.data?.employees || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.clear();
          window.location.assign('/login');
          return;
        }
        if (!cancelled)
          setError(err?.response?.data?.error || 'Could not load employee');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Projects this employee is a member of. Best-effort and separate from the
  // main load: a 403 (a member viewing a colleague) or a workspace error must
  // leave the rest of the profile intact.
  useEffect(() => {
    let cancelled = false;
    setProjectsState({ list: [], loading: true, denied: false });
    axios
      .get(`${API_URL}/api/workspace/projects?member_id=${id}`, auth())
      .then((r) => {
        if (cancelled) return;
        setProjectsState({
          list: Array.isArray(r.data) ? r.data : [],
          loading: false,
          denied: false,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectsState({
          list: [],
          loading: false,
          denied: err?.response?.status === 403,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Derived data — runs whether `member` is loaded or not (returns sane
  // fallbacks). Hooks must always run in the same order.
  const fullName = member
    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
    : '';
  // `/api/team/:id` returns `jobtitle` as a numeric FK. The org-chart employees
  // list (fetched alongside) carries the resolved job-title name, so prefer
  // that; fall back to any free-text designation, never the bare numeric id.
  const selfShaped = useMemo(
    () => employees.find((e) => e.id === member?.id) || null,
    [employees, member?.id]
  );
  const designation =
    selfShaped?.designation ||
    (member && !/^\d+$/.test(String(member.jobtitle || '')) ? member.jobtitle : '') ||
    member?.designation ||
    '';
  const department =
    selfShaped?.department ||
    (member && !/^\d+$/.test(String(member.department || '')) ? member.department : '') ||
    '';
  const tenure = useMemo(
    () => tenureFromJoin(member?.join_date || member?.doj),
    [member]
  );

  const photo = photoUrl(member?.profile_photo);
  // Navigating between employees must clear a previous load failure.
  useEffect(() => setPhotoFailed(false), [photo]);
  // Release the previous object URL whenever it is replaced or we unmount.
  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview]
  );

  // Skills — split the legacy comma/semicolon string and prefix each with @.
  const skills = useMemo(() => {
    if (!member?.skills) return [];
    return String(member.skills)
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [member?.skills]);

  // Manager + direct reports from the org-chart employees list.
  const managerInfo = useMemo(() => {
    if (!member?.primary_manager_id) return null;
    return employees.find((e) => e.id === member.primary_manager_id) || null;
  }, [employees, member?.primary_manager_id]);

  const directReports = useMemo(() => {
    if (!member) return [];
    return employees
      .filter((e) => e.primary_manager_id === member.id)
      .slice(0, 8);
  }, [employees, member]);

  const docs = useMemo(() => {
    if (!member) return [];
    return MEMBER_DOCUMENTS.filter((d) => member[d.field]);
  }, [member]);

  const isOwnRecord =
    ownMemberId != null && Number(ownMemberId) === Number(member?.id);
  const canSeeDocs = canManage || isOwnRecord;
  // Mirrors allowSelfOrManage on PATCH /api/team/:id/photo.
  const canChangePhoto = canManage || isOwnRecord;

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so re-picking the same file after a failure still fires.
    e.target.value = '';
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error('Profile photo must be a JPG or PNG image.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error('That photo is over the 10 MB limit — pick a smaller one.');
      return;
    }

    // Show the picked image straight away; revert it if the upload fails.
    const localUrl = URL.createObjectURL(file);
    setPhotoPreview(localUrl);
    setPhotoFailed(false);
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('profile_photo', file);
      const res = await axios.patch(
        `${API_URL}/api/team/${id}/photo`,
        fd,
        auth()
      );
      const saved = res.data?.profile_photo;
      if (saved) {
        setMember((m) => (m ? { ...m, profile_photo: saved } : m));
        // Drop the preview so what's on screen is the stored file.
        setPhotoPreview(null);
        // Keep the header avatar in step when it's the signed-in user's photo.
        if (isOwnRecord) {
          try {
            const ud = JSON.parse(localStorage.getItem('userdata') || '{}');
            ud.profile_photo = saved;
            localStorage.setItem('userdata', JSON.stringify(ud));
            window.dispatchEvent(new Event('userdataChanged'));
          } catch {
            /* header will catch up on next load */
          }
        }
      }
      toast.success('Profile photo updated.');
    } catch (err) {
      setPhotoPreview(null);
      toast.error(apiErrorMsg(err, 'Could not update the photo.'));
    } finally {
      setPhotoUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="ep-page">
        <p style={{ padding: 32 }}>Loading profile…</p>
      </div>
    );
  }
  if (error || !member) {
    return (
      <div className="ep-page">
        <p style={{ padding: 32, color: '#dc2626' }}>
          ⚠ {error || 'Employee not found'}
        </p>
        <Link to={`${base}/teams`}>← Back to employees</Link>
      </div>
    );
  }

  const firstName = fullName.split(' ')[0] || 'them';

  return (
    <div className="ep-page">
      <div className="ep-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count =
            t.key === 'documents' && canSeeDocs ? ` (${docs.length})` : '';
          return (
            <button
              key={t.key}
              type="button"
              className={`ep-tab ${activeTab === t.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <Icon size={14} /> {t.label}{count}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <>
          <section className="ep-columns">
            {/* ROW 1 — photo card | personal & work info | emergency + last 30
                days. All three are grid items in the same row, so they stretch
                to a common height and share one bottom edge. */}
            <div className="ep-photo-card">
              <div className="ep-photo-bg">
                {photoPreview || (photo && !photoFailed) ? (
                  <img
                    className="ep-photo-img"
                    src={photoPreview || photo}
                    alt={fullName || 'Profile photo'}
                    onError={() => setPhotoFailed(true)}
                  />
                ) : (
                  <span className="ep-photo-initials">{initialsOf(fullName)}</span>
                )}
              </div>
              <div className="ep-photo-top">
                <button
                  type="button"
                  className="ep-photo-btn"
                  onClick={() => navigate(`${base}/teams`)}
                  title="Back to employees"
                  aria-label="Back to employees"
                >
                  <ArrowLeft size={14} />
                </button>
                <div className="ep-photo-actions">
                  {canChangePhoto && (
                    <>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={onPickPhoto}
                        hidden
                      />
                      <button
                        type="button"
                        className="ep-photo-btn"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={photoUploading}
                        title="Change photo"
                        aria-label="Change profile photo"
                      >
                        {photoUploading ? (
                          <Loader2 size={14} className="ep-spin" />
                        ) : (
                          <Camera size={14} />
                        )}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="ep-photo-btn"
                    onClick={() => {
                      const url = window.location.href;
                      if (navigator.share) {
                        navigator.share({ title: fullName, url }).catch(() => {});
                      } else {
                        navigator.clipboard?.writeText(url);
                      }
                    }}
                    title="Share profile"
                    aria-label="Share profile"
                  >
                    <Share2 size={14} />
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      className="ep-photo-edit"
                      onClick={() => navigate(`${base}/teams/${id}/edit`)}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="ep-photo-overlay">
                <h1>{fullName || 'Unnamed'}</h1>
                <span className="ep-photo-role">{designation || 'Team member'}</span>
              </div>
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><ShieldCheck size={14} /></span>
                Personal &amp; work info
                <span className="ep-pill ep-pill-live">LIVE DATA</span>
              </h3>
              <dl className="ep-info-grid">
                <div className="ep-info-item">
                  <dt>Employee ID</dt>
                  <dd>EM-{String(member.id).padStart(4, '0')}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Work Shift</dt>
                  <dd>{member.work_shift || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Date of Joining</dt>
                  <dd>{fmtDate(member.join_date || member.doj)}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Tenure</dt>
                  <dd>{tenure.short}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Date of Birth</dt>
                  <dd>{fmtDate(member.dob)}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Gender</dt>
                  <dd>{member.gender || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Nationality</dt>
                  <dd>{member.nationality || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Department</dt>
                  <dd>{department || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Blood Group</dt>
                  <dd>{member.blood_group || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Address</dt>
                  <dd>{member.address || '—'}</dd>
                </div>
              </dl>
              <div className="ep-info-rows">
                <div className="ep-info-row">
                  <span className="ep-info-row-label"><Mail size={12} /> Email</span>
                  <span className="ep-info-row-value">
                    <a href={`mailto:${member.email}`}>{member.email}</a>
                  </span>
                </div>
                {parseAlternateEmails(member.alternate_emails).map((alt) => (
                  <div className="ep-info-row" key={alt}>
                    <span className="ep-info-row-label"><Mail size={12} /> Alternate</span>
                    <span className="ep-info-row-value">
                      <a href={`mailto:${alt}`}>{alt}</a>
                    </span>
                  </div>
                ))}
                {member.contact_number && (
                  <div className="ep-info-row">
                    <span className="ep-info-row-label"><Phone size={12} /> Phone</span>
                    <span className="ep-info-row-value">{member.contact_number}</span>
                  </div>
                )}
                {member.work_location && (
                  <div className="ep-info-row">
                    <span className="ep-info-row-label"><MapPin size={12} /> Location</span>
                    <span className="ep-info-row-value">{member.work_location}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ep-side-stack">
              <div className="ep-card ep-card-warm">
                <h3 className="ep-card-head">
                  <span className="ep-card-icon is-danger"><Phone size={14} /></span>
                  Emergency contact details
                </h3>
                <div className="ep-emergency-rows">
                  <div className="ep-emergency-row">
                    <span className="ep-emergency-label"><User size={12} /> Name</span>
                    <span className="ep-emergency-value">
                      {member.emergency_contact_name || '—'}
                    </span>
                  </div>
                  <div className="ep-emergency-row">
                    <span className="ep-emergency-label"><Phone size={12} /> Phone</span>
                    <span className="ep-emergency-value">
                      {member.emergency_contact_phone || '—'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ep-card">
                <h3 className="ep-card-head">
                  <span className="ep-card-icon"><Calendar size={14} /></span>
                  Last 30 days
                </h3>
                <AttendanceHeatmap days={30} />
              </div>
            </div>

            {/* ROW 2 — status | skills & tags | team */}
            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><BarChart3 size={14} /></span>
                Status
                <span className="ep-pill ep-pill-illustrative">ILLUSTRATIVE</span>
              </h3>
              {/* Placeholders until attendance/leave/task data is wired up.
                  Swap each `value` for the real figure then — StatTile animates
                  any finite number and prints anything else verbatim. */}
              <div className="ep-stat-grid">
                <StatTile icon={Clock} value={STAT_PLACEHOLDER} label="Hours this month" />
                <StatTile icon={Calendar} value={STAT_PLACEHOLDER} label="Leaves used" />
                <StatTile icon={Flame} value={STAT_PLACEHOLDER} label="Day streak" />
                <StatTile icon={CheckCircle} value={STAT_PLACEHOLDER} label="On-time rate" />
                <StatTile icon={Briefcase} value={STAT_PLACEHOLDER} label="Tenure" />
                <StatTile icon={ListChecks} value={STAT_PLACEHOLDER} label="Tasks done" />
              </div>
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><Tag size={14} /></span>
                Skills &amp; tags
                <span className="ep-pill ep-pill-live">LIVE DATA</span>
              </h3>
              {skills.length === 0 ? (
                <p className="ep-muted">No skills recorded yet.</p>
              ) : (
                <div className="ep-tag-cloud">
                  {skills.map((s) => (
                    <span key={s} className="ep-tag-pill">
                      @{s.replace(/^@/, '').replace(/\s+/g, '-').toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><Users size={14} /></span>
                Team
                <span className="ep-pill ep-pill-live">LIVE DATA</span>
              </h3>
              <div className="ep-team-label">REPORTS TO</div>
              {managerInfo ? (
                <Link to={`${base}/teams/${managerInfo.id}`} className="ep-team-row">
                  <span
                    className="ep-team-avatar"
                    style={{ background: teamAvatarColor(managerInfo.id) }}
                  >
                    {initialsOf(managerInfo.name)}
                  </span>
                  <div>
                    <div className="ep-team-name">{managerInfo.name}</div>
                    <div className="ep-team-sub">
                      {managerInfo.designation || 'Manager'}
                    </div>
                  </div>
                </Link>
              ) : (
                <p className="ep-muted">No manager assigned.</p>
              )}
              {directReports.length > 0 && (
                <>
                  <div className="ep-team-label">DIRECT REPORTS · {directReports.length}</div>
                  <div className="ep-team-list">
                    {directReports.map((r) => (
                      <Link key={r.id} to={`${base}/teams/${r.id}`} className="ep-team-row">
                        <span
                          className="ep-team-avatar"
                          style={{ background: teamAvatarColor(r.id) }}
                        >
                          {initialsOf(r.name)}
                        </span>
                        <div>
                          <div className="ep-team-name">{r.name}</div>
                          <div className="ep-team-sub">
                            {r.designation || `Reports to ${firstName}`}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="ep-card ep-projects-full">
            <h3 className="ep-card-head">
              <span className="ep-card-icon"><Briefcase size={14} /></span>
              Current projects
              <span className="ep-pill ep-pill-live">LIVE DATA</span>
              {projectsState.list.length > 0 && (
                <span className="ep-project-count">{projectsState.list.length}</span>
              )}
            </h3>
            {projectsState.loading ? (
              <p className="ep-muted">Loading projects…</p>
            ) : projectsState.denied ? (
              <p className="ep-muted">
                Only Admin and HR can see another employee&apos;s projects.
              </p>
            ) : projectsState.list.length === 0 ? (
              <p className="ep-muted">
                {firstName} is not assigned to any projects.
              </p>
            ) : (
              <>
                {projectsState.list.map((p, i) => (
                  <ProjectRow
                    key={p.id}
                    name={p.name}
                    client={p.client_name}
                    pct={DUMMY_PROGRESS[i % DUMMY_PROGRESS.length]}
                  />
                ))}
                <p className="ep-project-note">
                  Project names are live; the progress figures are placeholders.
                </p>
              </>
            )}
          </section>
        </>
      )}

      {activeTab === 'documents' && (
        <section>
          {!canSeeDocs ? (
            <p className="ep-muted">You do not have access to this employee's documents.</p>
          ) : (
            <>
              <h2 className="ep-doc-heading">Verified Documents ({docs.length})</h2>
              {docs.length === 0 ? (
                <p className="ep-muted">No documents on file.</p>
              ) : (
                <div className="ep-doc-grid">
                  {docs.map((d) => (
                    <div key={d.field} className="ep-doc-card">
                      <span className="ep-doc-icon"><FileText size={18} /></span>
                      <div className="ep-doc-body">
                        <div className="ep-doc-title">{d.label}</div>
                        <div className="ep-doc-sub">{DOC_SUBTITLES[d.field] || 'Document'}</div>
                      </div>
                      <button
                        type="button"
                        className="ep-doc-download"
                        title={`Download ${d.label}`}
                        aria-label={`Download ${d.label}`}
                        onClick={() =>
                          openMemberDocument(member.id, d.field).catch((err) =>
                            toast.error(
                              err?.response?.status === 403
                                ? 'You do not have access to this document.'
                                : err?.response?.status === 404
                                  ? 'The document file is missing.'
                                  : apiErrorMsg(err, 'Could not open the document.'),
                            ),
                          )
                        }
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === 'timeline' && (
        <section className="ep-card">
          <div className="ep-soon">
            <span className="ep-soon-icon"><Activity size={20} /></span>
            <h3>Coming soon</h3>
            <p>Employee timeline &mdash; joins, leaves, letters and milestones &mdash; is on its way.</p>
          </div>
        </section>
      )}
    </div>
  );
}
